<template>
  <div :class="`assessment-container-${state}`" class="container full-width assessment-container">
    <survey-dev-panel
      v-if="devPanelActive"
      :open="devPanelOpen"
      :package-version="PACKAGE_VERSION"
      :build-time="BUILD_TIME"
      :locales="locales"
      :transition-duration-scales="transitionDurationScales"
      :disable-transitions="disableTransitions"
      :show-question-answer-ids="showQuestionAnswerIds"
      :jump-destinations="jumpDestinations"
      :progressing-to-jump-destination="progressingToJumpDestination"
      :animating-to="animatingTo"
      v-model:transition-duration-scale="transitionDurationScaleInner"
      v-model:disable-button-disappearance="disableButtonDisappearance"
      v-model:auto-answer="autoAnswer"
      v-model:auto-continue="autoContinue"
      v-model:jump-to-destination="jumpToDestination"
      @toggle="toggleDevPanel"
      @change-locale="changeLocale"
      @toggle-disable-transitions="toggleDisableTransitions"
      @auto-answer-once="doAutoAnswer"
      @toggle-show-question-answer-ids="toggleShowQuestionAnswerIds"
      @prefill="prefillAnswersFromCollection"
      @prefill-trigger="prefillAnswersFromTriggerCollection"
      @jump="jumpToQuestion"
      @progress-to-jump="progressToJumpQuestion"
      @animate-white="animateToWhite"
      @animate-blue="animateToBlue"
    />

    <app-button
      :state="bottomButtonStateCalc"
      :color="bottomButtonColor"
      position="fixed"
      @click="onClickBottomButton"
    >
      {{ bottomButtonText }}
    </app-button>

    <section class="section">
      <transition
        :duration="fadeLogo ? transitionDuration : 0"
        :name="fadeLogo ? 'slide-fade-top' : ''"
      >
        <div v-if="showLogo" class="text-center pt-4 logo-container">
          <no-drag-image
            class="logo" src="@/assets/logo-regular.png"
            @click="onClickLogo"
          />
        </div>
      </transition>

      <transition-group name="slide-fade-top" :duration="transitionDuration">
        <p v-if="loading" class="center-text loading-text">
          {{ $t('intro.loadingSurvey') }}
        </p>

        <p v-if="showIntroText" class="center-text">
          {{ $t('intro.welcomeHeadline') }}<br>
          <br>
          <markdown-text>{{ $t('intro.welcomeText') }}</markdown-text>
        </p>

        <p v-if="showCodePicker" class="center-text">
          <strong>{{ $t('intro.codePickerHeadline') }}</strong><br>
          <br>
          <markdown-text class="code-picker-text" style="margin-bottom: 30px;">
            {{ $t('intro.codePickerIntroText') }}
          </markdown-text>
          <markdown-text class="code-picker-extra mt-3">
            {{ $t('intro.codePickerIntroExtra') }}
          </markdown-text>

          <SegmentedCodeInput
            v-model="currentCode"
            :length="6"
            :numeric-only="true"
          />

          <transition name="fade">
            <p v-if="codeError" class="error-text mt-3">
              {{ codeError }}
            </p>
          </transition>
        </p>

        <p v-if="showTermsText" class="center-text">
          <strong>{{ $t('consent.headline') }}</strong><br>
          <br>
          <markdown-text class="consent-text" style="margin-bottom: 30px;">
            {{ $t('consent.consentText') }}
          </markdown-text>
          <markdown-text class="consent-extra mt-3">
            {{ $t('consent.consentExtra') }}
          </markdown-text>
        </p>

        <div v-if="state === 'assessment'">
          <p>{{ $t('intro.assessmentTime') }}</p>
        </div>
      </transition-group>

      <div
        v-if="state === 'editAnswers'"
        class="edit-answers-container"
      >
        <div class="container">
          <h1>{{ $t('misc.editAnswers') }}</h1>
          <answers-given
            :question-answers="questionAnswers"
            :show-debug-info="false"
            :show-edit-button="true"
            @edit-question-answers="onEditQuestionAnswers"
          />
        </div>
      </div>

      <div
        v-if="state === 'questionGroupIntroDescription'"
        class="question-group-intro-description"
      >
        <transition name="slide-fade-top" :duration="transitionDuration">
          <p v-show="showQuestionGroupIntroDescription">
            {{ currentQuestionGroupIntroDescription }}
          </p>
        </transition>
        <transition name="slide-fade-top" :duration="transitionDuration">
          <div v-show="showQuestionGroupIntroDescription" class="blue-logo-container">
            <no-drag-image src="@/assets/logo-round-white.png" />
          </div>
        </transition>
      </div>

      <div
        v-if="state === 'questionGroupIntroTitle' || showQuestionGroupIntroTitle"
        class="question-group-intro-title"
      >
        <transition name="slide-fade-top" :duration="transitionDuration">
          <h1 v-show="showQuestionGroupIntroTitle">
            {{ currentQuestionGroupIntroTitle }}
          </h1>
        </transition>
        <div :class="showQuestionGroupNumber ? 'fade-enter-active' : 'invisible-opacity'">
          <div class="mb-4">
            <animating-number
              :current="currentQuestionGroupNumber" :total="questionGroupTotal"
              theme="blue"
            />
          </div>
        </div>
        <transition name="slide-fade-top" :duration="transitionDuration">
          <div v-show="showQuestionGroupIntroTitle" class="blue-logo-container">
            <no-drag-image src="@/assets/logo-round-white.png" />
          </div>
        </transition>
        <!--<div>
          <animating-number :current="currentPageIndex === 0 ? 1 : 2" :total="20" :show-total="true" theme="blue" />
        </div>-->
        <!--<p :class="showQuestionGroupIntroText ? 'fade-enter-active' : 'invisible-opacity'">
          For the
          following questions, we ask you to consider your overall level of satisfaction and regrets
          with all your previous cosmetic interventions put together (but excluding re-surgeries,
          ref. question 3)
        </p>-->
      </div>

      <transition mode="out-in" name="fade" :duration="transitionDuration">
        <div v-if="state === 'questionIntro'" key="question-intro" class="question-intro">
          <transition name="slide-fade-top" :duration="transitionDuration">
            <h1 v-if="showQuestionIntroTitle">
              {{ $t('question.introHeadline') }}
            </h1>
          </transition>
          <div :class="showQuestionIntroNumber ? 'fade-enter-active' : 'invisible-opacity'">
            <animating-number
              :current="currentQuestionIndex.question + 1" :show-total="false"
              :total="1000" text-align="center" type="roman"
            />
          </div>
        </div>
        <div v-else-if="state === 'question'" key="question-container" class="question-container">
          <animating-progress-bar
            :current="currentQuestionProgressCurrent"
            :total="currentQuestionProgressTotal"
            :group-current="currentQuestionGroupNumber"
            :group-total="questionGroupTotal"
            :group-label="currentQuestionGroupProgressLabel"
            @go-back="onClickGoBack"
          />
          <div v-if="showQuestionAnswerIds" class="question-identifier">
            {{ `${currentQuestionGroup.identifier} / ${currentQuestion.identifier}` }}
          </div>
          <div
            v-if="currentQuestion && showQuestion" :key="currentQuestion.id"
            class="question-container-inner"
          >
            <p class="mt-3 mb-2 question" v-html="currentQuestionQuestionHtml" />
            <p
              v-if="currentQuestionHint" class="mb-2 question-hint"
              v-html="currentQuestionHintHtml"
            />
            <p
              v-if="currentQuestionHintAutoHtml" class="mb-2 question-hint question-hint-auto"
              v-html="currentQuestionHintAutoHtml"
            />
            <div v-if="currentQuestionType !== 'message'" class="input-container">
              <input-radial-slider
                v-if="currentQuestionType === 'scale' " v-model="currentValue"
                :total="100"
              />
              <input-bar-pill
                v-else-if="currentQuestionType === 'scaleSmall'"
                v-model="currentValue"
                :choices="choices"
              />
              <input-freetext-small
                v-else-if="currentQuestionType === 'freetextSmall'"
                v-model="currentValue"
              />
              <input-freetext-large
                v-else-if="currentQuestionType === 'freetextLarge'"
                v-model="currentValue"
              />
              <input-priority
                v-else-if="currentQuestionType === 'division'" v-model="currentValue"
                :choices="choices"
              />
              <div
                v-else-if="currentQuestionType === 'multipleChoice' || currentQuestionType === 'multipleAnswers'"
              >
                <input-treatment
                  v-if="staticChoices === enums.STATIC_ANSWERS_TREATMENTS"
                  v-model="currentValue"
                />
                <input-diagnosis
                  v-else-if="staticChoices === enums.STATIC_ANSWERS_DIAGNOSES"
                  v-model="currentValue"
                  @continue="forceContinue"
                />
                <input-multiple-choice
                  v-else-if="currentQuestionType === 'multipleChoice'"
                  v-model="currentValue" :choices="choices"
                />
                <input-multiple-answers
                  v-else-if="currentQuestionType === 'multipleAnswers'"
                  v-model="currentValue" :choices="choices"
                />
              </div>
              <div v-else>
                Could not find component by question type {{
                  currentQuestionType
                }}
              </div>
            </div>
          </div>
        </div>
        <div v-else-if="state === 'end'" key="end-container" class="end-container">
          <go-back-button theme="white" @go-back="onClickGoBack" />

          <div class="logo-round-container">
            <no-drag-image
              class="logo mt-4" src="@/assets/logo-round-white.png"
              @click="onClickLogo"
            />
          </div>
          <p class="end-headline">
            {{ $t('end.thankYou') }}
          </p>
          <!--<div v-if="surveyIdentifier === 'main'">
            <p>{{ $t('end.endInfo1') }}</p>
            <p>{{ $t('end.submissionInfo1') }}</p>
            <hr/>
          </div> REMOVED AS OF 2022-06-20 -->
          <p>{{ $t('end.endInfo2') }}</p>
          <p>{{ $t('end.submissionInfo2') }}</p>

          <transition name="slide-fade-top">
            <p v-if="submittingAnswers" class="center-text loading-text">
              {{ $t('survey.submittingAnswers') }}
            </p>
          </transition>
          <transition name="slide-fade-top">
            <div v-if="!submittingAnswers" class="mb-4">
              <app-button
                color="white"
                size="block"
                @click="onClickSendNow"
              >
                {{ $t('end.buttonSubmitAnswers') }}
              </app-button>
            </div>
          </transition>
          <transition name="slide-fade-top">
            <div v-if="!submittingAnswers">
              <app-button
                color="white"
                size="block"
                @click="onClickEditAnswers"
              >
                {{ $t('end.buttonEditAnswers') }}
              </app-button>
            </div>
          </transition>
          <transition name="slide-fade-top">
            <div v-if="!submittingAnswers" class="mt-4">
              <p>
                <markdown-text>{{ $t('end.endInfo3') }}</markdown-text>
              </p>
            </div>
          </transition>
        </div>
        <div v-else-if="state === 'xxsubmit'" key="submit-container" class="submit-container">
          <transition name="slide-fade-top">
            <h1 v-if="showSubmissionHeadline">
              {{ $t('end.headlineSubmitAnswers') }}
            </h1>
          </transition>

          <transition name="slide-fade-top">
            <p v-if="submittingAnswers" class="center-text loading-text">
              {{ $t('survey.submittingAnswers') }}
            </p>
          </transition>
          <transition name="slide-fade-top">
            <div v-if="showSubmissionInfo && !submittingAnswers">
              <p>{{ $t('end.submissionInfo1') }}</p>
              <p>{{ $t('end.submissionInfo2') }}</p>

              <div>
                <app-button
                  color="grey"
                  size="block"
                  @click="onClickSendNow"
                >
                  {{ $t('end.buttonSendNow') }}
                </app-button>
              </div>
              <div>
                <app-button
                  color="grey"
                  size="block"
                  @click="onClickTakeMeBack"
                >
                  {{ $t('end.buttonTakeMeBack') }}
                </app-button>
              </div>
            </div>
          </transition>
        </div>
      </transition>
      <div class="button-y-spacer" />
    </section>
  </div>
</template>

<script>

import { mapState } from 'pinia';
import { useSurveyStore } from '@/store';
import { marked } from 'marked';
import AnimatingNumber from '@/components/AnimatingNumber/AnimatingNumber.vue';
import NoDragImage from '@/components/common/NoDragImage.vue';
import InputBarPill from '@/components/Inputs/InputBarPill/InputBarPill.vue';
import InputRadialSlider from '@/components/Inputs/InputRadialSlider/InputRadialSlider.vue';
import InputMultipleChoice from '@/components/Inputs/InputMultipleChoice/InputMultipleChoice.vue';
import InputMultipleAnswers
  from '@/components/Inputs/InputMultipleAnswers/InputMultipleAnswers.vue';

import { timeoutPromise } from '@/utils/promiseUtil';
import InputPriority from '@/components/Inputs/InputPriority/InputPriority';
import {
  ASSESSMENT_STATE_END,
  ASSESSMENT_STATE_ASK_CODE,
  ASSESSMENT_STATE_INTRO,
  ASSESSMENT_STATE_INTRO_TERMS,
  ASSESSMENT_STATE_LOADING,
  ASSESSMENT_STATE_QUESTION,
  ASSESSMENT_STATE_QUESTION_GROUP_INTRO_DESCRIPTION,
  ASSESSMENT_STATE_QUESTION_GROUP_INTRO_TITLE,
  ASSESSMENT_STATE_QUESTION_INTRO,
  ASSESSMENT_STATE_SUBMIT,
  ASSESSMENT_STATE_SUMMARY,
} from '@/enum/AssessmentState';

import QuestionAnswersPrefillGreen from '@/store/static/question-answers-green.js';
import QuestionAnswersPrefillYellow from '@/store/static/question-answers-yellow.js';
import QuestionAnswersPrefillRed from '@/store/static/question-answers-red.js';

import QuestionAnswersPrefillNoTrigger from '@/store/static/question-answers-trigger.js';

import AppButton from '@/components/common/AppButton';
import InputTreatment from '@/components/Inputs/InputTreatment/InputTreatment';
import InputDiagnosis from '@/components/Inputs/InputDiagnosis/InputDiagnosis';
import AnimatingProgressBar from '@/components/AnimatingProgressBar/AnimatingProgressBar';
import locales from '@/lib/i18n/index.js';
import getConsole from '@/utils/getConsole';
import MarkdownText from '@/components/common/MarkdownText';
import {
  QUESTION_TYPE_DIVISION,
  QUESTION_TYPE_FREETEXT_LARGE,
  QUESTION_TYPE_FREETEXT_SMALL,
  QUESTION_TYPE_MESSAGE,
  QUESTION_TYPE_MULTIPLE_ANSWERS,
  QUESTION_TYPE_MULTIPLE_CHOICE,
  QUESTION_TYPE_SCALE,
  QUESTION_TYPE_SCALE_SMALL,
} from '@/enum/QuestionType';
import { pickRandom } from '@/utils/arrayUtil';
import { STATIC_ANSWERS_DIAGNOSES, STATIC_ANSWERS_TREATMENTS } from '@/enum/StaticAnswers';
import { BACKGROUND_BLUE, BACKGROUND_GREY } from '@/enum/Background';
import SummaryReport from '@/components/Report/SummaryReport';
import InputFreetextSmall from '@/components/Inputs/InputFreetext/InputFreetextSmall';
import InputFreetextLarge from '@/components/Inputs/InputFreetext/InputFreetextLarge';
import { dotTrim, padRight } from '@/utils/stringUtil';
import GoBackButton from '@/components/common/GoBackButton';

import AnswersGiven from '../components/Report/AnswersGiven';
import SurveyDevPanel from '@/components/DevComponent/SurveyDevPanel.vue';
import { ASSESSMENT_STATE_EDIT_ANSWERS } from '../enum/AssessmentState';
import SegmentedCodeInput from '@/components/common/SegmentedCodeInput.vue';

export default {
  name: 'SurveyView',
  components: {
    SegmentedCodeInput,
    GoBackButton,
    SummaryReport,
    AppButton,
    InputPriority,
    MarkdownText,
    AnimatingProgressBar,
    InputDiagnosis,
    InputTreatment,
    InputRadialSlider,
    InputBarPill,
    NoDragImage,
    AnimatingNumber,
    InputFreetextSmall,
    InputFreetextLarge,
    InputMultipleChoice,
    InputMultipleAnswers,
    AnswersGiven,
    SurveyDevPanel,
  },
  data() {
    return {
      state: ASSESSMENT_STATE_LOADING,

      currentQuestionGroupNumber: 0,
      currentQuestionNumber: 0,

      currentQuestionGroupIndex: -1,
      currentQuestionIndex: { question: -1, group: -1 },

      currentQuestionGroupQuestionSkipCount: 0,

      loading: true,

      fadeLogo: true,

      bottomButtonState: null,
      sectionState: null,

      showIntroText: false,
      showIntroBottomButton: false,
      showTransitionScreen: false,
      showTermsText: false,
      showCodePicker: false,

      showQuestionGroupIntroTitle: false,
      showQuestionGroupIntroDescription: false,
      showQuestionGroupNumber: false,

      questionGroupTotal: 0,
      currentQuestionGroupProgressLabel: null,

      showQuestionIntroTitle: false,
      showQuestionIntroNumber: false,

      bottomButtonColor: 'blue',

      choices: [],
      staticChoices: null,

      currentValue: null,

      currentQuestionQuestionHtml: null,
      currentQuestionHintHtml: null,

      locales: Object.keys(locales),

      idsAssoc: {
        questionGroups: {},
        questions: {},
        questionAnswers: {},
      },

      summaryInfo: {
        considerationLevel: null,
        highRiskIndicator: null,
        bddIndicator: null,
        impulsivityIndicator: null,
        advices: null,
        reportQuestionAnswers: null,
        showDebugInfo: null,
      },

      questionIds: [],

      enums: {
        STATIC_ANSWERS_DIAGNOSES,
        STATIC_ANSWERS_TREATMENTS,
        QUESTION_TYPE_DIVISION,
        QUESTION_TYPE_MESSAGE,
        QUESTION_TYPE_MULTIPLE_ANSWERS,
        QUESTION_TYPE_MULTIPLE_CHOICE,
        QUESTION_TYPE_SCALE,
        QUESTION_TYPE_SCALE_SMALL,
      },

      submittingAnswers: false,
      showSubmissionHeadline: false,
      showSubmissionInfo: false,
      showQuestion: false,

      // Dev stuff
      disableButtonDisappearance: false,
      devCounter: 0,
      devPanelActive: false,
      devPanelOpen: false,

      autoAnswer: false,
      autoContinue: false,

      autoAnswerTimeout: null,
      autoContinueTimeout: null,

      transitionDurationScales: [
        0.01, 0.1, 0.25,
        0.5, 1, 2,
        5, 10, 25,
        50,
      ],

      transitionDurationScaleInner: 1,

      stateConsole: getConsole('State', '#ffffff', '#555500'),
      autoConsole: getConsole('Auto', '#ffffff', '#005555'),
      animationConsole: getConsole('Animation', '#ffffff', '#550055'),

      currentQuestionProgressCurrent: 0,
      currentQuestionProgressTotal: 0,

      animatingTo: null,

      isCheckingCode: false,
      codeError: null,

      progressingToJumpDestination: false,
      jumpToDestination: '',
      jumpToOptGroups: [],

      isEditing: false,

      PACKAGE_VERSION: __PACKAGE_VERSION__,
      BUILD_TIME: __BUILD_TIME__,

      currentCode: '',
    };
  },
  computed: {
    ...mapState(useSurveyStore, [
      'submissionToken',
      'cliniqueId',
      'language',
      'questionGroups',
      'disableTransitions',
      'questionAnswers',
      'transitionDurationScale',
      'treatmentCategories',
      'diagnosisCategories',
      'questionVars',
      'showQuestionAnswerIds',
    ]),
    // Precomputed option list for the dev panel's question jumper, so the
    // SurveyDevPanel stays presentational (labels depend on parent logic).
    jumpDestinations() {
      return this.questionGroups.map((questionGroup) => ({
        index: questionGroup.index,
        label: this.getQuestionJumperGroupLabel(questionGroup),
        questions: questionGroup.questions.map((question) => ({
          index: question.index,
          value: `${questionGroup.index},${question.index}`,
          label: this.getQuestionJumperQuestionLabel(questionGroup, question),
        })),
      }));
    },
    currentQuestionHintAutoHtml() {
      if (!this.currentQuestion) {
        return null;
      }

      if (this.currentQuestion.type === QUESTION_TYPE_MULTIPLE_ANSWERS) {
        if (this.currentQuestion.answers === 'diagnoses') {
          return this.$t('question.hintDiagnoses');
        }
        return this.$t('question.hintMultipleAnswers');
      }

      if (this.currentQuestion.type === QUESTION_TYPE_DIVISION) {
        return this.$t('question.hintPrioritize');
      }

      return null;
    },
    autoAnswerTimeoutMS() {
      // 50 is too fast to see what's going on
      // 500 is a good number to see what's going on
      return this.disableTransitions ? 50 : 500;
    },
    autoContinueTimeoutMS() {
      // 50 is too fast to see what's going on
      // 1000 is a good number to see what's going on
      return this.disableTransitions ? 50 : 1000;
    },
    bottomButtonStateCalc() {
      if (this.animatingTo) {
        return this.bottomButtonState;
      }

      if (this.state === ASSESSMENT_STATE_END) {
        return 'hide';
      }

      if (this.state === ASSESSMENT_STATE_QUESTION) {
        if (!this.canContinue) {
          return 'hide';
        }
      }

      return this.bottomButtonState;
    },
    transitionDuration() {
      return this.disableTransitions ? 1 : (800 * this.transitionDurationScale);
    },

    canContinue() {
      if (this.state === ASSESSMENT_STATE_QUESTION) {
        if (this.currentQuestion) {
          if (this.currentQuestion.type === 'message') {
            // Messages don't need an answer
            return true;
          }
        } else {
          console.warn('There was no "currentQuestion" that canContinue could parse');
        }

        if (this.currentValue instanceof Array) {
          return this.currentValue.length > 0;
        }

        if (this.currentQuestion.type === 'scale') {
          return true; // Allowed to be 0
        }

        // Make sure the number is truthy
        return !!this.currentValue;
      }

      return true;
    },

    // Question groups
    currentQuestionGroup() {
      if (this.currentQuestionGroupIndex < 0) {
        return null;
      }

      return this.questionGroups[this.currentQuestionGroupIndex];
    },
    currentQuestionGroupIntroTitle() {
      return this.currentQuestionGroup?.title || '';
    },
    currentQuestionGroupIntroDescription() {
      return this.currentQuestionGroup?.description || '';
    },

    currentQuestion() {
      if (this.currentQuestionIndex.group < 0 || this.currentQuestionIndex.question < 0) {
        return null;
      }

      return this.questionGroups[this.currentQuestionIndex.group]?.questions[this.currentQuestionIndex.question];
    },
    currentQuestionQuestion() {
      return this.currentQuestion?.question || '';
    },
    currentQuestionHint() {
      return this.currentQuestion?.hint || '';
    },
    currentQuestionType() {
      return this.currentQuestion?.type || '';
    },

    showLogo() {
      return this.state === ASSESSMENT_STATE_LOADING || this.state === ASSESSMENT_STATE_ASK_CODE || this.state === ASSESSMENT_STATE_INTRO || this.state === ASSESSMENT_STATE_INTRO_TERMS;
    },
    bottomButtonText() {
      if (this.state === ASSESSMENT_STATE_ASK_CODE) {
        if (this.isCheckingCode) {
          return this.$t('intro.checkingCode');
        }

        return this.$t('intro.askCode');
      }
      if (this.state === ASSESSMENT_STATE_INTRO) {
        return this.$t('intro.welcomeContinue');
      }
      if (this.state === ASSESSMENT_STATE_INTRO_TERMS) {
        return this.$t('consent.buttonAgree');
      }
      if (this.state === ASSESSMENT_STATE_END) {
        return this.$t('end.buttonSubmitAnswers');
      }
      if (this.state === ASSESSMENT_STATE_SUMMARY) {
        return this.$t('end.buttonFullReport');
      }

      return this.$t('misc.next');
    },
    surveyIdentifier() {
      return this.$router.currentRoute?.value?.params?.surveyIdentifier;
    },
  },
  watch: {
    state() {
      if (this.state === ASSESSMENT_STATE_INTRO) {
        this.animateToIntro();
      }
    },
    currentQuestion() {
      // console.log('currentQuestion', this.currentQuestion);

      this.currentQuestionQuestionHtml = marked.parse(this.currentQuestionQuestion);
      this.currentQuestionHintHtml = marked.parse(this.currentQuestionHint);

      const answers = this.currentQuestion?.answers || [];

      const choices = [];
      let staticChoices;

      if (answers === STATIC_ANSWERS_DIAGNOSES || answers === STATIC_ANSWERS_TREATMENTS) {
        staticChoices = answers;
      } else {
        for (const answer of answers) {
          choices.push({
            label: answer.answer,
            value: answer.identifier,
          });
        }
      }

      this.choices = choices;
      this.staticChoices = staticChoices;

      let currentValue;

      if (this.currentQuestion && this.currentQuestion.id) {
        currentValue = this.getQuestionAnswerIdentifiersForQuestion(this.currentQuestion.id);
      } else {
        currentValue = [];
      }

      this.currentValue = currentValue;

      if (this.currentQuestion && this.currentQuestion.id) {
        if (!this.hasValue(this.currentValue) && this.autoAnswer) {
          if (this.currentQuestion.type !== QUESTION_TYPE_MESSAGE) {
            if (this.autoAnswerTimeout) {
              clearTimeout(this.autoAnswerTimeout);

              this.autoAnswerTimeout = null;
            }

            this.autoConsole.log(`Scheduling auto answer in ${this.autoAnswerTimeoutMS}ms`);

            this.autoAnswerTimeout = setTimeout(() => {
              this.doAutoAnswer();
            }, this.autoAnswerTimeoutMS);
          }
        }
      }

      this.updateQuestionProgressCurrent();
    },
    currentQuestionGroup() {
      this.currentQuestionGroupQuestionSkipCount = 0;

      if (!this.currentQuestionGroup.useCounter) {
        this.currentQuestionGroupProgressLabel = this.currentQuestionGroup.title;
      } else {
        this.currentQuestionGroupProgressLabel = null;
      }
    },
    transitionDurationScaleInner() {
      useSurveyStore().setTransitionDurationScale(this.transitionDurationScaleInner);
    },
    currentQuestionGroupNumber() {
      this.stateConsole.log(`Changed currentQuestionGroupNumber to ${this.currentQuestionGroupNumber}`);
    },
    currentQuestionNumber() {
      this.stateConsole.log(`Changed currentQuestionNumber to ${this.currentQuestionNumber}`);
    },
    currentQuestionGroupIndex() {
      this.stateConsole.log(`Changed currentQuestionGroupIndex to ${this.currentQuestionGroupIndex}`);
    },
    currentQuestionIndex() {
      this.stateConsole.log(`Changed currentQuestionIndex to ${this.currentQuestionIndex.group} // ${this.currentQuestionIndex.question}`);
    },
    bottomButtonState() {
      this.stateConsole.log(`Changed bottomButtonState to ${this.bottomButtonState}`);
    },
    currentCode(val) {
      if (this.state === ASSESSMENT_STATE_ASK_CODE) {
        this.codeError = null;

        if (val.length === 6) {
          this.bottomButtonState = 'peak';

          this.verifyCode();
        } else {
          this.bottomButtonState = 'hide';
        }
      }
    },
  },
  mounted() {
    if (process.env.VUE_APP_DEV_PANEL_DEFAULT && parseInt(process.env.VUE_APP_DEV_PANEL_DEFAULT, 10) > 0) {
      this.devPanelActive = true;
    }

    window.toggleDevPanel = (activate = null) => {
      if (activate === null) {
        this.devPanelActive = !this.devPanelActive;
      } else {
        this.devPanelActive = activate;
      }
    };

    // this.autoAnswer = true;
    // this.autoContinue = true;
    // this.toggleDisableTransitions();

    useSurveyStore().setBackground(BACKGROUND_GREY);

    // this.login();

    this.transitionDurationScaleInner = this.transitionDurationScale;

    this.loading = true;

    console.log('Loading..');

    const language = this.$i18n.locale;

    this.$apiClient.misc.getGameData(language).then((gameData) => {
      useSurveyStore().setTreatmentCategories(gameData.treatmentCategories);
      useSurveyStore().setDiagnosisCategories(gameData.diagnosisCategories);

      return this.$apiClient.survey.getSurvey(this.surveyIdentifier, language);
    }).then((surveyData) => {
      const clientUserType = window.submissionToken && window.submissionToken.length ? 'professional' : 'private';

      useSurveyStore().mutateQuestionVariable({
        variableName: 'isPrivateUse',
        callback: () => clientUserType === 'private',
      });

      useSurveyStore().mutateQuestionVariable({
        variableName: 'isProfessionalUse',
        callback: () => clientUserType === 'professional',
      });

      console.log('ClientUserType', clientUserType);
      console.log('isPrivateUse', this.questionVars.isPrivateUse);
      console.log('isProfessionalUse', this.questionVars.isProfessionalUse);

      useSurveyStore().setQuestionGroups(surveyData.questionGroups);

      if (surveyData.hasBeenFilled) {
        this.$router.push(`/survey/${this.surveyIdentifier}/report/${this.submissionToken}`);

        return;
      }

      this.questionIds = [];

      let questionGroupTotal = 0;

      for (const questionGroup of surveyData.questionGroups) {
        if (!questionGroup.identifier) {
          continue;
        }

        if (questionGroup.useCounter) {
          questionGroupTotal += 1;
        }

        const questionGroupIdentifierFull = questionGroup.identifier;

        this.idsAssoc.questionGroups[questionGroupIdentifierFull] = questionGroup.id;

        for (const question of questionGroup.questions) {
          if (!question.identifier) {
            continue;
          }

          const questionIdentifierFull = `${questionGroupIdentifierFull}/${question.identifier}`;

          this.idsAssoc.questions[questionIdentifierFull] = question.id;
          this.questionIds.push(question.id);

          if (question.answers && question.answers.length) {
            for (const answer of question.answers) {
              if (!answer.identifier) {
                continue;
              }

              const questionAnswerIdentifierFull = `${questionIdentifierFull}/${answer.identifier}`;

              this.idsAssoc.questionAnswers[questionAnswerIdentifierFull] = answer.id;
            }
          }
        }
      }

      this.questionGroupTotal = questionGroupTotal;
      this.currentQuestionProgressTotal = this.questionIds.length;

      this.$nextTick(() => {
        console.log('Loaded..');

        this.loading = false;

        if (this.cliniqueId && !this.submissionToken) {
          this.state = ASSESSMENT_STATE_ASK_CODE;
          this.showCodePicker = true;
        } else {
          this.onIntroContinue();
        }
      });
    });
  },
  methods: {
    onIntroContinue() {
      if (this.surveyIdentifier === 'main') {
        this.state = ASSESSMENT_STATE_INTRO;
      } else {
        this.fadeLogo = false;

        this.animateToQuestionGroupIntroDescription(() => {
          this.showLogo = false;
          this.showIntroText = false;
        });
      }
    },
    verifyCode() {
      if (this.isCheckingCode) {
        return;
      }

      this.isCheckingCode = true;

      this.$apiClient.clinique.lookUpCliniqueCode(this.cliniqueId, this.currentCode, true)
        .then((response) => {
          if (response && response.token) {
            useSurveyStore().setSubmissionToken(response.token);
            this.$apiClient.setBearerToken(response.token);

            this.onClickBottomButton(true);
          } else {
            this.codeError = this.$t('intro.invalidCode');
          }
        })
        .catch((e) => {
          console.error(e);

          this.codeError = this.$t('intro.invalidCode');
        })
        .finally(() => {
          this.isCheckingCode = false;
        });
    },

    /* <editor-fold desc="[Generic animation methods]"> */
    animateTo(color, middleCallback = null, peakAfter = true, animateIfColorAlreadyMatches = true) {
      return new Promise((resolve, reject) => {
        if (this.animatingTo) {
          const message = `Already animating to ${this.animatingTo} so cannot call animateTo again`;

          this.animationConsole.warn(message);

          reject(message);
        }

        if (this.bottomButtonColor === color) {
          // The button was the same color as the background we want to go to, let's move it up
          this.bottomButtonState = 'transition-up-show';
        } else {
          if (!animateIfColorAlreadyMatches) {
            if (middleCallback) {
              middleCallback();
            }

            resolve();

            return;
          }

          // The button was the opposite color of the background we want to go to, so let's hide it
          this.bottomButtonState = 'hide';
        }

        this.animatingTo = color;

        if (this.disableButtonDisappearance) {
          useSurveyStore().setBackground(color === 'white' ? BACKGROUND_GREY : BACKGROUND_BLUE);

          return;
        }

        timeoutPromise(this.transitionDuration)
          .then(() => {
            if (middleCallback) {
              middleCallback();
            }
          })
          .then(() => {
            useSurveyStore().setBackground(color === 'white' ? BACKGROUND_GREY : BACKGROUND_BLUE);

            this.bottomButtonState = 'hide-instant';

            return timeoutPromise(50);
          })
          .then(() => {
            this.bottomButtonColor = color === 'white' ? 'blue' : 'white';

            this.animatingTo = null;

            if (peakAfter) {
              this.bottomButtonState = 'peak';
            }

            return timeoutPromise(peakAfter ? this.transitionDuration : 0);
          })
          .then(() => {
            resolve();
          });
      });
    },
    animateToWhite(middleCallback = null, peakAfter = true, animateIfColorAlreadyMatches = true) {
      this.animationConsole.log('animateToWhite - start');

      return this.animateTo('white', middleCallback, peakAfter, animateIfColorAlreadyMatches).then(() => {
        this.animationConsole.log('animateToWhite - end');
      });
    },
    animateToBlue(middleCallback = null, peakAfter = true, animateIfColorAlreadyMatches = true) {
      this.animationConsole.log('animateToBlue - start');
      return this.animateTo('blue', middleCallback, peakAfter, animateIfColorAlreadyMatches).then(() => {
        this.animationConsole.log('animateToBlue - end');
      });
    },
    /* </editor-fold> */

    getQuestionGroupProgressNumber(questionGroup) {
      const questionGroupIdentifierNice = questionGroup.identifier;

      if (questionGroupIdentifierNice !== 'intro') {
        return `Q${questionGroupIdentifierNice}`;
      }

      return questionGroupIdentifierNice;
    },
    getQuestionJumperGroupLabel(questionGroup) {
      const questionGroupIdentifierNice = this.getQuestionGroupProgressNumber(questionGroup);

      return `${questionGroupIdentifierNice} / ${questionGroup.title}`;
    },
    getQuestionJumperQuestionLabel(questionGroup, question) {
      const padNoBreak = (str, len) => {
        const noBreakSpaceUnicodeCharacter = ' '; // Note: this is NOT a space, it's

        return padRight(str, len, noBreakSpaceUnicodeCharacter);
      };

      const questionGroupIdentifier = padNoBreak(this.getQuestionJumperGroupLabel(questionGroup), 2);
      const questionIdentifier = padNoBreak(question.identifier, 5);
      const questionTrimmed = dotTrim(question.question, 80);

      return `[${questionGroupIdentifier} / ${questionIdentifier}] ${questionTrimmed}`;
    },

    progressToJumpQuestion() {
      if (!this.jumpToDestination || this.progressingToJumpDestination) {
        return;
      }

      this.autoAnswer = true;
      this.autoContinue = true;
      useSurveyStore().setDisableTransitions(true);

      this.progressingToJumpDestination = true;

      this.forceContinue();
    },
    jumpToQuestion() {
      if (!this.jumpToDestination || this.progressingToJumpDestination) {
        return;
      }

      this.isEditing = false;

      const [questionGroupIndex, questionIndex] = this.jumpToDestination
        .split(',')
        .map((v) => parseInt(v, 10));

      this.openQuestion(questionGroupIndex, questionIndex);

      this.$snackbar.add({
        type: 'info',
        text: `Jumped to question ${questionGroupIndex}, ${questionIndex}`,
      });
    },
    openQuestion(questionGroupIndex, questionIndex) {
      this.state = ASSESSMENT_STATE_QUESTION;

      this.showIntroText = false;
      this.showIntroBottomButton = false;
      this.showTransitionScreen = false;
      this.showTermsText = false;
      this.showCodePicker = false;
      this.showQuestionGroupIntroTitle = false;
      this.showQuestionGroupIntroDescription = false;
      this.showQuestionGroupNumber = false;
      this.showQuestionIntroTitle = false;
      this.showQuestionIntroNumber = false;
      this.showSubmissionHeadline = false;
      this.showSubmissionInfo = false;
      this.showQuestion = true;

      this.bottomButtonColor = 'blue';
      this.bottomButtonState = 'peak';

      useSurveyStore().setBackground(BACKGROUND_GREY);

      this.currentQuestionGroupIndex = questionGroupIndex;

      this.updateCurrentQuestionGroupNumber();
      this.currentQuestionNumber = questionIndex + 1;

      this.currentQuestionIndex.group = questionGroupIndex;
      this.currentQuestionIndex.question = questionIndex;
    },
    prefillAnswersFromCollection(collection) {
      if (collection === 'green') {
        this.prefillAnswers(QuestionAnswersPrefillGreen[0]);
      } else if (collection === 'yellow') {
        this.prefillAnswers(QuestionAnswersPrefillYellow[0]);
      } else if (collection === 'red') {
        this.prefillAnswers(QuestionAnswersPrefillRed[0]);
      } else {
        console.log(`Unknown prefill collection "${collection}"`);
      }
    },
    prefillAnswersFromTriggerCollection(trigger) {
      this.prefillAnswers(QuestionAnswersPrefillNoTrigger[trigger]);
    },
    prefillAnswers(answers) {
      console.log('Prefilling with answers', answers);

      // Auto load
      for (const questionId of Object.keys(answers)) {
        const questionAnswer = answers[questionId];

        useSurveyStore().setQuestionAnswer({
          questionId,
          answer: questionAnswer,
        });
      }

      this.isEditing = false;
      this.fadeLogo = false;
      this.showIntroText = false;
      this.showIntroBottomButton = false;
      this.showTransitionScreen = false;
      this.showTermsText = false;
      this.submittingAnswers = false;
      this.showSubmissionHeadline = false;
      this.showSubmissionInfo = false;

      this.currentQuestionGroupIndex = this.questionGroups.length - 1;
      this.currentQuestionIndex.group = this.currentQuestionGroupIndex;
      this.currentQuestionIndex.question = this.questionGroups[this.currentQuestionGroupIndex].questions.length - 1;

      this.$nextTick(() => {
        this.animateToEnd();
      });
    },
    onClickSendNow() {
      this.submittingAnswers = true;

      this.$apiClient.survey.submitAnswers(this.surveyIdentifier, this.questionAnswers, !this.submissionToken).then((result) => {
        if (result.verification) {
          this.$apiClient.console.log(`Set verification to ${result.verification}`);

          this.$apiClient.setDefaultHeader('X-Token-Verification', result.verification);
        } else {
          this.$apiClient.console.log('Removed verification');

          this.$apiClient.setDefaultHeader('X-Token-Verification', null);
        }

        if (result.submissionToken) {
          this.$apiClient.console.log(`Set submission token ${result.submissionToken}`);

          useSurveyStore().setSubmissionToken(result.submissionToken);
          this.$apiClient.setBearerToken(result.submissionToken);
        } else {
          this.$apiClient.console.log('Did not set a bearer token as non were defined');
        }

        if (this.surveyIdentifier === 'main') {
          this.$router.push(`/survey/${this.surveyIdentifier}/summary/${result.submissionToken}`);
        } else {
          this.$router.push(`/survey/${this.surveyIdentifier}/thank-you`);
        }
      });
    },
    onClickTakeMeBack() {
      this.animateToEnd();
    },
    onEditQuestionAnswers({ questionGroup, question }) {
      this.state = ASSESSMENT_STATE_QUESTION;
      this.isEditing = true;

      this.openQuestion(questionGroup.index, question.index);
    },
    onClickEditAnswers(instant) {
      if (instant) {
        this.state = ASSESSMENT_STATE_EDIT_ANSWERS;
      } else {
        this.animateToWhite(() => {
          this.state = ASSESSMENT_STATE_EDIT_ANSWERS;
        });
      }
      /*

      // this.state = 'question';
      this.currentQuestionIndex.question = 0;
      this.currentQuestionIndex.group = 0;
      this.currentQuestionGroupIndex = 0;

      timeoutPromise(0).then(() => {
        this.state = 'endTransitionToQuestion';

        return timeoutPromise(this.transitionDuration);
      }).then(() => {
        this.animateToQuestionGroupIntroDescription();
      }); */
    },
    doAutoAnswer() {
      const questionId = this.currentQuestion.id;
      const questionType = this.currentQuestion.type;

      if (this.hasValue(this.currentValue)) {
        this.autoConsole.warn(`Question ${questionId} (${questionType}) already has a current value`, this.currentValue);
        // return;
      }

      const newValue = this.getRandomAnswers(this.currentQuestion);

      if (!newValue || !newValue.length) {
        this.autoConsole.warn(`Could not fill question ${questionId} (${questionType}) since the random answers was empty`, this.currentQuestion);
        this.autoConsole.log('newValue', newValue);

        return;
      }

      this.autoConsole.log(`Filling out question ${questionId} (${questionType}) with answer ${JSON.stringify(newValue)}`);

      this.currentValue = newValue;
    },
    autoContinueClickButton() {
      if (this.autoContinueTimeout) {
        clearTimeout(this.autoContinueTimeout);

        this.autoContinueTimeout = null;
      }

      this.autoConsole.log(`Scheduling auto continue in ${this.autoContinueTimeoutMS}ms`);

      this.autoContinueTimeout = setTimeout(() => {
        this.autoConsole.log('Clicking on continue..');

        this.onClickBottomButton();
      }, this.autoContinueTimeoutMS);
    },
    hasValue(currentValue) {
      if (currentValue instanceof Array) {
        return currentValue.length > 0;
      }

      return !!currentValue;
    },
    getRandomAnswers(question) {
      const answers = this.getRandomAnswersInner(question);

      // console.log('question.type', question.type, answers);

      return answers;
    },
    getRandomAnswersInner(question) {
      const currentAnswers = [];

      if (question.answers === STATIC_ANSWERS_DIAGNOSES) {
        // Special case for diagnosis
        const maxReturnedItems = 10;

        for (const diagnosisCategory of this.diagnosisCategories) {
          for (const diagnosis of diagnosisCategory.items) {
            if (currentAnswers.length >= maxReturnedItems) {
              break;
            }

            if (!diagnosis.id) {
              continue;
            }

            if (Math.random() <= 0.5) {
              currentAnswers.push(diagnosis.id);
            }
          }
        }
      } else if (question.answers === STATIC_ANSWERS_TREATMENTS) {
        // Special case for treatments
        const maxReturnedItems = 10;

        for (const treatmentCategory of this.treatmentCategories) {
          for (const treatment of treatmentCategory.items) {
            if (currentAnswers.length >= maxReturnedItems) {
              break;
            }

            if (!treatment.id) {
              continue;
            }

            if (Math.random() <= 0.5) {
              currentAnswers.push(treatment.id);
            }
          }
        }
      } else if (question.type === QUESTION_TYPE_MULTIPLE_CHOICE || question.type === QUESTION_TYPE_SCALE_SMALL) {
        // Pick out a single random answer
        const randomAnswer = pickRandom(question.answers);

        if (!randomAnswer) {
          throw new Error(`Could not generate random answer for question ${question.id}`);
        }

        currentAnswers.push(randomAnswer.identifier);
      } else if (question.type === QUESTION_TYPE_MULTIPLE_ANSWERS) {
        // Pick out multiple random answers

        const maxReturnedItems = Math.floor(question.answers.length / 2);

        do {
          for (const answer of question.answers) {
            if (currentAnswers.length >= maxReturnedItems) {
              break;
            }

            if (!answer.id) {
              continue;
            }

            if (Math.random() <= 0.5) {
              currentAnswers.push(answer.identifier);
            }
          }
        } while (currentAnswers.length === 0);
      } else if (question.type === QUESTION_TYPE_SCALE) {
        // Pick out a random number
        currentAnswers.push(Math.max(0, Math.min(100, Math.round(Math.random() * 100))));
      } else if (question.type === QUESTION_TYPE_DIVISION) {
        for (const answer of question.answers) {
          currentAnswers.push(answer.identifier);
        }

        // Randomize
        currentAnswers.sort(() => ((Math.random() > 0.5) ? 1 : -1));
      } else if (question.type === QUESTION_TYPE_FREETEXT_SMALL) {
        currentAnswers.push(this.generateRandomString(200));
      } else if (question.type === QUESTION_TYPE_FREETEXT_LARGE) {
        currentAnswers.push(this.generateRandomString(800));
      }

      if (!currentAnswers.length) {
        throw new Error('Could not generate a current answer');
      }

      return currentAnswers;
    },
    toggleShowQuestionAnswerIds() {
      useSurveyStore().setShowQuestionAnswerIds(!this.showQuestionAnswerIds);
    },
    generateRandomString(len) {
      let sentence = '';

      while (sentence.length < len) {
        let word = '';

        for (let i = 0, wordLen = Math.random() * 10; i < wordLen; i++) {
          word += Math.random() * 65;
        }

        sentence = (sentence ? `${sentence} ` : '') + word;
      }

      return sentence.substr(0, len);
    },
    updateQuestionProgressCurrent() {
      let progress = 0;

      if (this.currentQuestion && this.questionIds.length) {
        const index = this.questionIds.indexOf(this.currentQuestion.id);

        if (index >= 0) {
          progress = index;
        }
      }

      progress = Math.max(0, progress);

      this.currentQuestionProgressCurrent = progress;
    },
    getQuestionAnswerIdentifiersForQuestion() {
      const questionAnswerQuestionIds = Object.keys(this.questionAnswers).map((v) => parseInt(v, 10));
      const questionId = this.currentQuestion.id;

      // console.log('questionAnswerKeys', this.questionAnswers);
      // console.log('questionAnswerQuestionIds', questionAnswerQuestionIds);
      // console.log('questionId', questionId);
      // console.log('questionAnswerKeys.includes(questionId)', questionAnswerQuestionIds.includes(questionId));

      const currentValue = [];

      if (questionAnswerQuestionIds.includes(questionId)) {
        const questionLookup = this.getQuestionById(questionId);
        const questionAnswers = questionLookup.answers;

        const staticValueTypes = [
          QUESTION_TYPE_SCALE,
          QUESTION_TYPE_FREETEXT_SMALL,
          QUESTION_TYPE_FREETEXT_SMALL,
        ];

        if (staticValueTypes.includes(this.currentQuestion.type)) {
          for (const value of this.questionAnswers[questionId]) {
            currentValue.push(value);
          }
        } else if (typeof (questionAnswers) === 'string' || questionAnswers.length <= 0) {
          // Treatments/diagnoses/static lists - just use raw values
          for (const value of this.questionAnswers[questionId]) {
            currentValue.push(value);
          }
        } else {
          for (const value of this.questionAnswers[questionId]) {
            for (const answer of questionAnswers) {
              if (answer.id === value) {
                currentValue.push(answer.identifier);

                break;
              }
            }
          }
        }
      }

      console.log('currentValue', currentValue);

      return currentValue;
    },
    onClickGoBack() {
      if (this.isEditing) {
        this.onClickEditAnswers(true);
      } else {
        this.animateToWhite(() => {
          let newQuestionIndex = this.currentQuestionIndex.question;
          let newQuestionGroupIndex = this.currentQuestionIndex.group;

          let dependencyMatch = null;

          do {
            newQuestionIndex -= 1;

            if (newQuestionIndex < 0) {
              newQuestionGroupIndex -= 1;
              newQuestionIndex = this.questionGroups[newQuestionGroupIndex].questions.length - 1;

              if (newQuestionGroupIndex < 0) {
                console.warn('Cannot go back to a previous question group as there is none');

                return;
              }
            }

            const previousQuestion = this.questionGroups[newQuestionGroupIndex].questions[newQuestionIndex] || null;

            dependencyMatch = previousQuestion ? this.getDependencyMatch(previousQuestion) : null;
          } while (dependencyMatch === false && (newQuestionIndex >= 0 || newQuestionGroupIndex >= 0));

          this.currentQuestionGroupIndex = newQuestionGroupIndex;
          this.currentQuestionIndex.group = newQuestionGroupIndex;
          this.currentQuestionIndex.question = newQuestionIndex;

          // Reset question stuff to make sure we're not in some weird state
          this.state = ASSESSMENT_STATE_QUESTION;

          this.showIntroText = false;
          this.showIntroBottomButton = false;
          this.showTransitionScreen = false;
          this.showTermsText = false;
          this.showQuestionGroupIntroTitle = false;
          this.showQuestionGroupIntroDescription = false;
          this.showQuestionGroupNumber = false;
          this.showQuestionIntroTitle = false;
          this.showQuestionIntroNumber = false;
          this.showSubmissionHeadline = false;
          this.showSubmissionInfo = false;
          this.showQuestion = true;

          this.updateCurrentQuestionGroupNumber();

          console.log(`New question index: ${this.currentQuestionIndex.group} / ${this.currentQuestionIndex.question}`);
        }, true, false);
      }
    },
    toggleDisableTransitions() {
      useSurveyStore().setDisableTransitions(!this.disableTransitions);
    },
    changeLocale(locale) {
      this.$i18n.locale = locale;

      useSurveyStore().setLanguage(locale === 'system' ? 'en' : locale);
    },
    forceContinue() {
      this.onClickBottomButton(true);
    },
    toggleDevPanel() {
      this.devPanelOpen = !this.devPanelOpen;
    },
    onClickLogo() {
      if (process.env.VUE_APP_BUILD_ENVIRONMENT === 'prod') {
        return; // Do nothing in prod builds
      }

      if (this.devPanelActive) {
        // Dev panel already active

        return;
      }

      this.devCounter += 1;

      if (this.devCounter === 5) {
        this.$snackbar.add({
          type: 'info',
          text: 'You are now a dev',
        });

        this.devPanelActive = true;
        this.devPanelOpen = false;
      }
    },
    onClickBottomButton(forced = false) {
      if (!forced && !this.canContinue) {
        console.warn('Cannot continue right now because canContinue is false');

        return;
      }

      let promise;

      if (this.state === ASSESSMENT_STATE_EDIT_ANSWERS) {
        this.state = ASSESSMENT_STATE_END;
        useSurveyStore().setBackground(BACKGROUND_BLUE);
      } else if (this.state === ASSESSMENT_STATE_ASK_CODE) {
        if (!forced) {
          this.verifyCode();

          return;
        }

        this.showCodePicker = false;

        this.onIntroContinue();
      } else if (this.state === ASSESSMENT_STATE_INTRO) {
        this.showIntroText = false;
        this.showIntroBottomButton = false;

        promise = this.animateToIntroTerms();
      } else if (this.state === ASSESSMENT_STATE_INTRO_TERMS) {
        this.fadeLogo = false;
        this.showTermsText = false;

        promise = this.animateToQuestionGroupIntroDescription();
      } else if (this.state === ASSESSMENT_STATE_QUESTION_GROUP_INTRO_DESCRIPTION) {
        promise = this.animateToQuestionGroupIntroTitle();
      } else if (this.state === ASSESSMENT_STATE_QUESTION_GROUP_INTRO_TITLE) {
        promise = this.animateToQuestionIntro();
      } else if (this.state === ASSESSMENT_STATE_QUESTION_INTRO) {
        promise = this.animateToQuestion();
      } else if (this.state === ASSESSMENT_STATE_QUESTION) {
        if (this.currentQuestion.type === QUESTION_TYPE_MESSAGE || this.saveQuestionAnswer()) {
          // Messages obviously have no answer, don't even save it
          if (this.isEditing) {
            const questionsDependingOnThis = this.getQuestionsDependingOnQuestionId(this.currentQuestion.id);

            console.log('question', this.currentQuestion, 'questionsDependingOnThis', questionsDependingOnThis);

            let missingDependencyQuestion;

            // First check if there's any other questions that depend on this
            if (questionsDependingOnThis) {
              for (const dependencyQuestion of questionsDependingOnThis) {
                if (!this.isAnswerableQuestion(dependencyQuestion.question)) {
                  continue;
                }

                const result = this.getDependencyMatch(dependencyQuestion.question);

                if (result === true) {
                  console.log('A direct dependency question is relevant', dependencyQuestion, result);

                  if (this.questionIsAnswered(dependencyQuestion.question)) {
                    continue; // Already answered
                  }

                  // Not answered, let's go to it!!
                  console.log('A direct dependency is NOT answered!!');
                  missingDependencyQuestion = dependencyQuestion;

                  break;
                }
              }
            }

            if (!missingDependencyQuestion) {
              // Then let's check if any answer sets any variable, so we can recalculate our other dependencies

              const currentQuestionAnswerIds = this.questionAnswers[this.currentQuestion.id]
                ? this.questionAnswers[this.currentQuestion.id]
                : [];

              const currentQuestionAnswerObjects = [];

              if (currentQuestionAnswerIds) {
                for (const currentQuestionAnswerId of currentQuestionAnswerIds) {
                  for (const answer of this.currentQuestion.answers) {
                    if (answer.id === currentQuestionAnswerId) {
                      currentQuestionAnswerObjects.push(answer);

                      break;
                    }
                  }
                }
              }

              let answerMutatesVars = false;

              if (currentQuestionAnswerObjects.length) {
                for (const currentQuestionAnswerObject of currentQuestionAnswerObjects) {
                  if (currentQuestionAnswerObject.mutateVars && currentQuestionAnswerObject.mutateVars.length) {
                    answerMutatesVars = true;

                    break;
                  }
                }
              }

              console.log('currentQuestionAnswers', currentQuestionAnswerIds, currentQuestionAnswerObjects, answerMutatesVars);

              if (answerMutatesVars) {
                for (const questionGroup of this.questionGroups) {
                  for (const question of questionGroup.questions) {
                    if (!question.dependency) {
                      continue;
                    }

                    if (!question.dependency.evaluateVars) {
                      continue;
                    }

                    if (question.dependency.evaluateVars.length === 0) {
                      continue;
                    }

                    if (!this.getDependencyMatch(question)) {
                      continue; // Question still not relevant
                    }

                    // Question is relevant!
                    if (!this.questionIsAnswered(question)) {
                      missingDependencyQuestion = {
                        questionGroup,
                        question,
                      };

                      break;
                    }
                  }
                }
              }
            }

            // TODO: Check if any answers are setting variables, because then we need to run through evaluateVars!

            if (missingDependencyQuestion) {
              this.openQuestion(missingDependencyQuestion.questionGroup.index, missingDependencyQuestion.question.index);
            } else {
              // All dependencies are met, let's just go back to the edit screen
              this.onClickEditAnswers(true);
            }
            console.log('questionsDependingOnThis', questionsDependingOnThis);
            console.log('missingDependencyQuestion', missingDependencyQuestion);

            /*

            const missingDependencyQuestion = this.getFirstQuestionWithMissingAnswers();
            //const missingDependencyQuestion = null;

            if (missingDependencyQuestion) {
              console.log('WHAT IS GOING ON???? THERE IS A QUESTION MISSING DEPENDENCY !!! FIX IT', missingDependencyQuestion);

            } else {
              //console.log('onClickEditAnswersTrue!!!');
              this.onClickEditAnswers(true);
            } */

            return;
          }
          promise = this.animateToQuestionIntro();
        } else {
          console.warn('Could not call saveQuestionAnswer (it returned false)');
        }
      } else if (this.state === ASSESSMENT_STATE_END) {
        // promise = this.animateToSubmit();
        this.onClickSendNow();

        return;
      } else if (this.state === ASSESSMENT_STATE_SUMMARY) {
        this.$router.push('/report');

        return;
      }

      if (promise && this.autoContinue) {
        promise.then(() => {
          this.stateConsole.log('Current state: ', this.state, {
            currentQuestionGroupIndex: this.currentQuestionGroupIndex,
            currentQuestionIndex: this.currentQuestionIndex,
          });

          if (this.jumpToDestination && this.progressingToJumpDestination) {
            const [jumpQuestionGroupIndex, jumpQuestionIndex] = this.jumpToDestination
              .split(',')
              .map((v) => parseInt(v, 10));

            let stopReason;

            if (this.currentQuestionIndex.group === jumpQuestionGroupIndex) {
              if (this.currentQuestionIndex.question === jumpQuestionIndex) {
                stopReason = `Stopping auto progress since we hit ${jumpQuestionGroupIndex}, ${jumpQuestionIndex}`;
              } else if (this.currentQuestionIndex.question > jumpQuestionIndex) {
                stopReason = `Stopping auto progress since we are somehow past ${jumpQuestionGroupIndex}, ${jumpQuestionIndex} (same group; probably missed a dependency)`;
              }
            } else if (this.currentQuestionIndex.group > jumpQuestionGroupIndex) {
              stopReason = `Stopping auto progress since we are somehow past ${jumpQuestionGroupIndex}, ${jumpQuestionIndex} (next group; probably missed a dependency)`;
            }

            if (stopReason) {
              stopReason = `${stopReason} (current index: ${this.currentQuestionIndex.group}, ${this.currentQuestionIndex.question})`;

              this.autoAnswer = false;
              this.autoContinue = false;
              useSurveyStore().setDisableTransitions(false);

              this.$snackbar.add({
                type: 'info',
                text: stopReason,
              });

              this.stateConsole.log(stopReason);

              this.progressingToJumpDestination = false;

              return;
            }
          }

          this.autoContinueClickButton();
        });
      }
    },
    saveQuestionAnswer() {
      const currentAnswers = [];

      if (this.currentValue instanceof Array) {
        for (const value of this.currentValue) {
          currentAnswers.push(value);
        }
      } else {
        currentAnswers.push(this.currentValue);
      }

      // console.log('currentAnswers', currentAnswers.length);
      if (currentAnswers.length === 0) {
        console.warn('Could not save question answer as currentAnswers.length was 0');

        return false;
      }

      const currentAnswerIds = [];
      const answerType = typeof this.currentQuestion.answers;

      if (answerType === 'string') {
        console.log('CURRENT ANSWER IS STRING', answerType);
        // Eg. treatments/diagnoses
        for (const answer of currentAnswers) {
          currentAnswerIds.push(answer);
        }
      } else if (this.currentQuestion.answers && this.currentQuestion.answers.length) {
        // currentAnswerIds.push(answer.identifier)
        if (this.currentQuestion.type === 'division') {
          for (const givenAnswer of currentAnswers) {
            for (const currentQuestionAnswer of this.currentQuestion.answers) {
              if (givenAnswer === currentQuestionAnswer.identifier) {
                currentAnswerIds.push(currentQuestionAnswer.id);
              }
            }
          }
        } else {
          for (const answer of this.currentQuestion.answers) {
            if (currentAnswers.includes(answer.identifier)) {
              currentAnswerIds.push(answer.id);
            }
          }
        }
      } else {
        // Probably a raw response like a scale or freetext
        // console.error('Could not find answer ids from answers', answerType, currentAnswers);
        if (currentAnswers instanceof Array) {
          for (const answer of currentAnswers) {
            currentAnswerIds.push(answer);
          }
        } else {
          currentAnswerIds.push(currentAnswers);
        }
      }

      if (currentAnswerIds.length > 0) {
        const currentAnswerObjects = {};

        console.log('currentAnswerIds', currentAnswerIds);

        if (this.currentQuestion.answers) {
          for (const currentAnswerId of currentAnswerIds) {
            let currentAnswerObject;

            for (const answer of this.currentQuestion.answers) {
              if (answer.id === currentAnswerId) {
                currentAnswerObject = answer;

                break;
              }
            }

            if (currentAnswerObject) {
              currentAnswerObjects[currentAnswerId] = currentAnswerObject;
            }
          }

          const currentAnswerObjectKeys = Object.keys(currentAnswerObjects);

          if (currentAnswerObjectKeys.length) {
            const mutateVarRegex = /^([a-zA-Z0-9\-]+)\s*([\*\+\-\/=]+)\s*(.+)$/;

            for (const answerId in currentAnswerObjects) {
              const currentAnswerObject = currentAnswerObjects[answerId];

              if (currentAnswerObject && currentAnswerObject.mutateVars) {
                for (const mutateVar of currentAnswerObject.mutateVars) {
                  const match = mutateVar.match(mutateVarRegex);

                  if (match.length > 0) {
                    const mutationVarName = match[1];
                    const mutationVarOperator = match[2];

                    // eslint-disable-next-line no-eval
                    const mutationVarValueRaw = eval(match[3]);

                    useSurveyStore().mutateQuestionVariable({
                      variableName: mutationVarName,
                      callback: (currentValue) => {
                        const previousValue = currentValue;

                        if (currentValue === null) {
                          const mutationVarValueType = typeof (mutationVarValueRaw);

                          if (mutationVarValueType === 'string') {
                            currentValue = '';
                          } else if (mutationVarValueType === 'boolean') {
                            currentValue = false;
                          } else if (mutationVarValueType === 'number') {
                            currentValue = 0;
                          }
                        }

                        if (mutationVarOperator === '=') {
                          currentValue = mutationVarValueRaw;
                        } else if (mutationVarOperator === '+=') {
                          currentValue += mutationVarValueRaw;
                        } else if (mutationVarOperator === '-=') {
                          currentValue -= mutationVarValueRaw;
                        } else if (mutationVarOperator === '/=') {
                          currentValue /= mutationVarValueRaw;
                        } else if (mutationVarOperator === '*=') {
                          currentValue *= mutationVarValueRaw;
                        }

                        console.log('mutationVarName', mutationVarName);
                        console.log('mutationVarOperator', mutationVarOperator);
                        console.log('mutationVarValueRaw', mutationVarValueRaw);
                        console.log('previousValue', previousValue);
                        console.log('newValue', currentValue);

                        return currentValue;
                      },
                    });
                  }
                }
              }
            }
          }
        }
      }

      useSurveyStore().setQuestionAnswer({
        questionId: this.currentQuestion.id,
        answer: currentAnswerIds,
      });

      console.log('SET_QUESTION_ANsWER', {
        questionId: this.currentQuestion.id,
        answer: currentAnswerIds,
        currentQuestion: this.currentQuestion,
        currentQuestionType: this.currentQuestion.type,
        currentAnswers: JSON.parse(JSON.stringify(this.currentQuestion.answers)),
      });

      this.clearIrrelevantDependencyAnswers(this.currentQuestion.id);

      return true;
    },
    questionIsAnswered(question, questionAnswerIds = null) {
      if (questionAnswerIds === null) {
        questionAnswerIds = Object.keys(this.questionAnswers);
      }

      const questionIdString = question.id.toString();

      // This question doesn't rely on any other questions, it MUST be answered
      if (!questionAnswerIds.includes(questionIdString)) {
        return false; // Normal question is missing an answer
      }

      return true;
    },
    getFirstQuestionWithMissingAnswers() {
      const questionAnswerIds = Object.keys(this.questionAnswers);

      if (!this.questionGroups) {
        console.warn('No question groups!');

        return false;
      }

      // console.log('questionGroup', this.questionGroups);

      for (const questionGroup of this.questionGroups) {
        if (!questionGroup.questions) {
          console.warn('No questions for question group!', questionGroup);

          continue;
        }

        // console.log('questionGroup.questions', questionGroup.questions);

        for (const question of questionGroup.questions) {
          if (!this.isAnswerableQuestion(question)) {
            continue;
          }

          if (question.dependency) {
            // Only check main questions with no dependencies
            continue;
          }

          if (!this.questionIsAnswered(question, questionAnswerIds)) {
            return question;
          }

          // Alright, we have all the answers to _THIS_ question, but what about questions depending on this?
          // No need to check further than 1 level as the answers would be removed
          const questionsDependingOnThis = this.getQuestionsDependingOnQuestionId(question.id);

          console.log('question', question, 'questionsDependingOnThis', questionsDependingOnThis);

          if (questionsDependingOnThis) {
            for (const dependencyQuestion of questionsDependingOnThis) {
              if (!this.isAnswerableQuestion(dependencyQuestion)) {
                continue;
              }

              const result = this.getDependencyMatch(dependencyQuestion);

              if (result === true) {
                console.log('A direct dependency question is relevant', dependencyQuestion, result);

                if (this.questionIsAnswered(dependencyQuestion)) {
                  continue; // Already answered
                }

                // Not answered, let's go to it!!
                console.log('A direct dependency is NOT answered!!');
                return dependencyQuestion;
              }
            }
          }
        }
      }

      return null;
    },
    isAnswerableQuestion(question) {
      return question.type !== QUESTION_TYPE_MESSAGE;
    },
    getQuestionsDependingOnQuestionId(questionId) {
      const dependingQuestions = [];

      questionId = questionId.toString();

      for (const questionGroup of this.questionGroups) {
        for (const question of questionGroup.questions) {
          const { dependency } = question;

          if (!dependency) {
            // No dependency, so obviously it's not depending on another question
            continue;
          }

          if (dependency.evaluateVars) {
            // Can't compute evaluate vars here
            continue;
          }

          if (dependency.questionId.toString() !== questionId) {
            // Not related to the question at hand
            continue;
          }

          dependingQuestions.push({
            questionGroup,
            question,
          });
        }
      }

      return dependingQuestions;
    },
    clearIrrelevantDependencyAnswers(questionId) {
      for (const questionGroup of this.questionGroups) {
        for (const question of questionGroup.questions) {
          if (!question.dependency) {
            continue;
          }

          const { dependency } = question;

          if (!dependency.evaluateVars) {
            if (dependency.questionId !== questionId) {
              continue;
            }
          }

          console.log('clearIrrelevantDependencyAnswers!!!');

          const dependencyMatch = this.getDependencyMatch(question);

          if (dependency.evaluateVars) {
            console.log('OOOOOOO', dependency.evaluateVars);
            console.log('OOOOOO1', dependencyMatch);
          }

          if (dependencyMatch) {
            // Dependency match is still true, do nothing
            console.log('dependency match (still) true! do nothing');
          } else {
            // Check if we currently even have an answer, as it might be missing (and then we'd end up with a recursive loop because of evaluateVars)
            const questionAnswerIds = Object.keys(this.questionAnswers);

            const questionIdString = question.id.toString();

            if (questionAnswerIds.includes(questionIdString)) {
              console.log('DEPENDENCY MATCH NO LONGER MATCHING FOR QUESTION', question);
              console.log('question', question);
              console.log('dependencyMatch', dependencyMatch);
              console.log('REMOVING ANSWERS');

              useSurveyStore().removeQuestionAnswer(question.id);
              // delete this.questionAnswers[question.id];

              // Just in case that the dependency affects other dependencies..
              this.clearIrrelevantDependencyAnswers(question.id);
            } else {
              console.log(`We do not have an answer for question with question id ${question.id}`);
              console.log('questionIdString', questionIdString);
              console.log('questionAnswerIds', questionAnswerIds);
            }
          }
        }
      }
    },
    getDependencyMatch(question) {
      const { dependency } = question;

      if (!dependency) {
        return null;
      }

      if (dependency.evaluateVars) {
        for (const evaluateVars of dependency.evaluateVars) {
          const questionVars = {};

          for (const questionVar in this.questionVars) {
            const questionVarValue = this.questionVars[questionVar];

            questionVars[questionVar] = questionVarValue;
          }

          // console.log('EVALUATE VARS!!! 1', evaluateVars);
          // console.log('EVALUATE VARS!!! 2', dependency);

          // console.log('EVALUATE VARS!!! 3', this.questionVars);
          // console.log('EVALUATE VARS!!! 4', questionVars);

          // eslint-disable-next-line no-eval
          const evaluateVarsResult = eval(evaluateVars);
          // console.log('EVALUATE VARS!!! 5', evaluateVarsResult);

          if (!evaluateVarsResult) {
            return false;
          }
        }

        return true;
      }

      const dependencyAnswerIds = dependency.answerIds && dependency.answerIds.length ? dependency.answerIds : null;
      const dependencyQuestionId = dependency.questionId || null;
      const dependencyNegate = dependency.negate || false;

      if (!this.questionAnswers) {
        console.error('Could not find any question answers');

        return null;
      }

      const questionAnswerKeys = Object.keys(this.questionAnswers).map((v) => parseInt(v, 10));
      const questionHasAnswer = questionAnswerKeys.includes(dependencyQuestionId);

      if (!questionHasAnswer) {
        const dependencyQuestion = this.getQuestionById(dependencyQuestionId);

        console.error(`Could not find question answers of dependency question ${dependencyQuestionId}`, question, this.questionAnswers, questionAnswerKeys, dependencyQuestion);

        return false;
      }

      const dependencyQuestionAnswerIds = questionHasAnswer
        ? this.questionAnswers[dependencyQuestionId]
        : [];

      let dependencyAnswerMatch = false;

      for (const dependencyQuestionAnswerId of dependencyQuestionAnswerIds) {
        if (dependencyAnswerIds && dependencyAnswerIds.includes(dependencyQuestionAnswerId)) {
          dependencyAnswerMatch = true;
        }
      }

      const dependencyAnswerMatchPre = dependencyAnswerMatch;

      if (dependencyNegate) {
        dependencyAnswerMatch = !dependencyAnswerMatch;
      }

      console.log('dependencyQuestion', {
        question,
        dependencyAnswerMatchPre,
        dependencyAnswerMatch,
        dependencyQuestionAnswerIds,
        dependencyAnswerIds,
        dependencyQuestionId,
        questionAnswerKeys,
        questionHasAnswer,
      });

      if (dependencyAnswerMatch === false) {
        // The dependency answer is NOT part of the match

        // this.currentQuestionIndex = nextQuestionIndex;

        return false;
      }
      return true;
    },
    getQuestionById(questionId) {
      for (const questionGroup of this.questionGroups) {
        for (const question of questionGroup.questions) {
          if (question.id === questionId) {
            return question;
          }
        }
      }

      console.warn(`getQuestionById - could not find question by id ${questionId}`);

      return null;
    },

    /* <editor-fold description="Other animation methods"> */
    animateToIntro() {
      this.animationConsole.log('animateToIntro');

      this.showIntroText = false;

      this.bottomButtonState = null;

      timeoutPromise(this.transitionDuration)
        .then(() => {
          this.showIntroText = true;

          return timeoutPromise(this.transitionDuration);
        })
        .then(() => {
          this.bottomButtonState = 'peak';

          return true;
        });
    },
    animateToIntroTerms() {
      this.animationConsole.log('animateToIntroTerms');

      this.bottomButtonState = 'hide';

      return timeoutPromise(this.transitionDuration)
        .then(() => {
          this.state = ASSESSMENT_STATE_INTRO_TERMS;

          return timeoutPromise(this.transitionDuration);
        }).then(() => {
          this.showTermsText = true;

          return timeoutPromise(this.transitionDuration);
        }).then(() => {
          // this.bottomButtonColor = 'white';
          this.bottomButtonState = 'peak';
        });
    },
    animateToEnd() {
      this.animationConsole.log('animateToEnd');

      return this.animateToBlue(() => {
        this.state = ASSESSMENT_STATE_END;
      });
    },
    animateToSubmit() {
      this.animationConsole.log('animateToSubmit');

      this.animateToBlue(() => {
        this.state = ASSESSMENT_STATE_SUBMIT;
      })
        .then(() => {
          this.showSubmissionHeadline = true;

          return timeoutPromise(800 * this.transitionDurationScale);
        })
        .then(() => {
          this.showSubmissionInfo = true;

          return true;
        });
    },
    animateToQuestionGroupIntroDescription(middleCallback = null) {
      this.animationConsole.log('animateToQuestionGroupIntroDescription');

      const nextQuestionGroupIndex = this.currentQuestionGroupIndex + 1;

      if (nextQuestionGroupIndex >= this.questionGroups.length) {
        this.stateConsole.log(`Reached end, the next question group index (${nextQuestionGroupIndex}) does not exist`);

        return this.animateToEnd();
      }

      this.currentQuestionGroupIndex = nextQuestionGroupIndex;

      const nextQuestionGroup = this.questionGroups[nextQuestionGroupIndex] || null;

      // this.currentQuestionGroupNumber = this.currentQuestionGroupIndex + 1;

      if (!nextQuestionGroup.description) {
        this.stateConsole.log('There was no description, so we\'re continuing directly to the title');

        // No description, move straight onto showing the title
        return this.animateToQuestionGroupIntroTitle();
      }

      this.showQuestionGroupIntroDescription = false;

      this.stateConsole.log('Showing description for question group', nextQuestionGroup);
      this.stateConsole.log(' - Current button state', this.bottomButtonState);
      this.stateConsole.log(' - Current button color', this.bottomButtonColor);
      this.stateConsole.log(' - Current state', this.state);

      const questionWasShowing = this.showQuestion;

      return this.animateToBlue(() => {
        if (middleCallback) {
          middleCallback();
        }

        this.state = ASSESSMENT_STATE_QUESTION_GROUP_INTRO_DESCRIPTION;

        if (questionWasShowing) {
          this.showQuestion = false;
        }

        this.showQuestionGroupIntroDescription = true;
      });
    },
    animateToQuestionGroupIntroTitle() {
      this.animationConsole.log('animateToQuestionGroupIntroTitle');

      // currentQuestionGroupIndex has already increased, so we're setting the number to the previous number
      // this.currentQuestionGroupNumber = this.currentQuestionGroupIndex;
      const { currentQuestionGroup } = this;

      if (!currentQuestionGroup) {
        this.stateConsole.warn('Could not animate to current question group as there is none');

        return timeoutPromise(0);
      }

      this.stateConsole.log('Showing description for question group', currentQuestionGroup);
      this.stateConsole.log(' - Current button state', this.bottomButtonState);
      this.stateConsole.log(' - Current button color', this.bottomButtonColor);
      this.stateConsole.log(' - Current state', this.state);

      if (!this.currentQuestionGroup.title) {
        this.currentQuestionIndex.question = -1;
        this.currentQuestionIndex.group = this.currentQuestionGroupIndex;

        this.updateCurrentQuestionGroupNumber();

        this.bottomButtonState = 'peak';

        return this.animateToQuestionIntro();
      }

      return this.animateToBlue(() => {
        this.state = ASSESSMENT_STATE_QUESTION_GROUP_INTRO_TITLE;
      }, false)
        .then(() => {
          this.showQuestionGroupIntroTitle = true;

          return timeoutPromise(this.transitionDuration);
        })
        .then(() => {
          if (this.currentQuestionGroup.useCounter) {
            this.showQuestionGroupNumber = true;

            return timeoutPromise(this.transitionDuration);
          }

          return timeoutPromise(0);
        })
        .then(() => {
          let questionGroupCounterSkipCount = 0;

          // Reset, seeing as we're going to a new group
          for (const questionGroup of this.questionGroups) {
            if (questionGroup.index === this.currentQuestionGroupIndex) {
              break;
            }

            if (!questionGroup.useCounter) {
              questionGroupCounterSkipCount += 1;
            }
          }
          this.currentQuestionIndex.question = -1;
          this.currentQuestionIndex.group = this.currentQuestionGroupIndex;
          // this.currentQuestionIndex = -1;
          this.updateCurrentQuestionGroupNumber();

          if (this.currentQuestionGroup.useCounter) {
            return timeoutPromise(this.transitionDuration);
          }

          return timeoutPromise(0);
        })
        .then(() => {
          this.bottomButtonState = 'peak';
        });
    },
    updateCurrentQuestionGroupNumber() {
      if (this.surveyIdentifier === 'main') {
        // 0 = demographics, so the others are equal to their respective number
        this.currentQuestionGroupNumber = this.currentQuestionGroupIndex;
      } else {
        // All others just use the index + 1
        this.currentQuestionGroupNumber = this.currentQuestionGroupIndex + 1;
      }
    },
    animateToQuestionIntro(nextIterator = 1) {
      this.animationConsole.log('animateToQuestionIntro', nextIterator);

      this.currentQuestionNumber = this.currentQuestionIndex.question;

      const nextQuestionIndex = this.currentQuestionIndex.question + nextIterator;

      const nextQuestion = this.questionGroups[this.currentQuestionIndex.group]?.questions[nextQuestionIndex] || null;

      if (!nextQuestion) {
        // No more questions
        return this.animateToQuestionGroupIntroDescription();
      }

      const dependencyMatch = this.getDependencyMatch(nextQuestion);

      if (dependencyMatch === false) {
        // We don't fullfill the dependency for this question, so let's instantly move to the next one
        return this.animateToQuestionIntro(nextIterator + 1);
      }

      return this.animateToWhite(() => {
        this.state = ASSESSMENT_STATE_QUESTION;

        this.showQuestionGroupIntroTitle = false;
        this.showQuestionGroupNumber = false;

        this.currentQuestionIndex.question = nextQuestionIndex;
        this.currentQuestionIndex.group = this.currentQuestionGroupIndex;

        this.showQuestion = true;
      }, true, false);
    },
    animateToQuestion(middleCallback = null) {
      this.animationConsole.log('animateToQuestion');

      return this.animateToWhite(() => {
        this.state = ASSESSMENT_STATE_QUESTION;

        this.showQuestionGroupIntroTitle = false;
        this.showQuestionGroupNumber = false;

        if (middleCallback) {
          middleCallback();
        }
      });
    },
    /* </editor-fold> */
  },
};
</script>

<style lang="scss" scoped>
.error-text {
  color: #ff4d4d;
  font-weight: bold;
}

.question-hint {
  font-style: italic;
  color: lighten(#212529, 20%);
}

.logo {
  max-width: 100%;
}

p {
  font-size: 1.2em;
  padding: 0 30px;

  &.center-text {
    margin-top: 40px;
    text-align: center;
  }
}

section {
  z-index: 1;
}

.button-y-spacer {
  height: 65px;
}

.container, section {
  height: 100%;
}

.container {
  padding-left: 0;
  padding-right: 0;

  &.assessment-container-questionGroupIntroDescription,
  &.assessment-container-questionGroupIntroTitle,
  &.assessment-container-end {
    background-color: #45A4CD;
  }
}

section {
  padding-left: 15px;
  padding-right: 15px;
}

.question-group-intro-title,
.question-group-intro-description {
  //padding-top: 100px;
  position: absolute;
  top: 50%;
  left: 50%;
  width: 100%;
  height: auto;
  transform: translate(-50%, -50%);
  z-index: 10000;

  h1 {
    text-align: center;
    font-weight: bold;
    margin-bottom: 70px;
  }

  h1, p {
    color: #EDF1F4;
  }

  p {
    padding: 0 35px;
    font-size: 1.2em;
  }
}

.end-container {
  text-align: center;
  color: #EDF1F4;

  .end-headline {
    font-weight: bold;
  }

  .go-back-container {
    position: absolute;
    top: -15px;
    left: 5px;
  }
}

.submit-container {
  h1 {
    font-size: 24pt;
    font-weight: bold;
    text-align: center;
    padding-bottom: 15vh;
    padding-top: 15vh;
  }

  p, div {
    width: 100%;
    max-width: 500px;
    margin: 0 auto;
    padding: 0;
  }

  p {
    margin-bottom: 40px;
  }

  .app-button {
    margin-top: 20px;
  }
}

.question-group-intro-description {
  p {
    text-align: center;
  }
}

.question-intro {
  //padding-top: 100px;
  position: absolute;
  top: 50%;
  left: 50%;
  width: 100%;
  height: auto;
  transform: translate(-50%, -50%);

  h1 {
    text-align: center;
    font-weight: bold;
    margin-bottom: 70px;
  }

  h1, div {
    color: #063C5A;
  }

  div {
    padding: 0 35px;
    margin: 0 auto;
  }
}

.question {
  padding: 0;
}

.full-width {
  max-width: 100% !important;
}

.progress-bar-container {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
}

.question-container {
  // Fix progress bar bouncing on transition
  &.fade-enter-active, &.fade-leave-active {
    // Padding to keep the content as is
    padding-right: 15px;
    padding-left: 15px;

    // Margin to fix the progress bar
    margin-left: -15px;
    margin-right: -15px;
  }

  padding-bottom: 50px;
  padding-top: 50px;
}

.question-identifier {
  position: fixed;
  top: 400px;
  right: 0;
  padding: 10px;
  font-weight: bold;
  background: rgba(200, 200, 255, 80%);
}

.content-extra {
  color: #aa0000;
  font-style: italic;
}

.blue-logo-container, .logo-round-container {
  text-align: center;

  img {
    max-width: 130px;
    //max-width: 25%;
  }
}

.question-group-intro-title {
  h1 {
    margin-bottom: 30px;
  }
}

.logo-container {
  padding: 0 60px;
}
</style>

<style lang="scss">.segmented-code-input {
  --field-size: 60px;
  --field-font: 30px;

  display: flex;
  justify-content: center;
  align-items: center;
  flex-wrap: nowrap;
  gap: 10px;

  .segmented-code-input__field {
    width: var(--field-size);
    height: var(--field-size);
    border: 1px solid #ccc;
    border-radius: 4px;
    text-align: center;
    font-size: var(--field-font);
    font-weight: bold;
    line-height: 30px;
    background: white;
  }

  @media (max-width: 550px) {
    --field-size: 50px;
  }

  @media (max-width: 400px) {
    --field-size: 45px;
    margin-left: -30px;
    margin-right: -30px;
    justify-content: space-between;
    gap: 0;
  }

  @media (max-width: 300px) {
    --field-size: 30px;
    --field-font: 20px;
  }
}
</style>
